import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120lSchFService } from './f1120l-sch-f.service';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120l-sch-f',
    templateUrl: './f1120l-sch-f.component.html',
    styleUrls: ['./f1120l-sch-f.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120lSchFComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Modal properties
  modalTitle = "Schedule F Increase (Decrease) in Reserves (section 807) and Company/Policyholder Share Percentage (section 812) (see instructions)";

  // Data properties
  netReserves = 0;
  batchLoader = false;
  showBatchData = false;
  showRequestLoader = true;
  showFormLoader = true;
  
  scheduleFFormView: any = { rows: [] };
  formListOriginal: any = { rows: [] };
  unallocatedAmountOriginal: any = [[], []]; // Store original unallocated amount for reset
  
  formData: any = {
    addrows: [],
    editrows: [],
    removerows: []
  };

  // Entity properties
  leId: string = '';
  formName: string = '1120L';
  cdr_no: string = '';
  leType: string = '';
  systemFlag: string = 'D';
  hgt: string = '96%';

  // Batch data
  BatchList: any[] = [];
  BatchListData: any[] = [];
  prev_index: any = null;

  // Entity list
  entityList: any[] = [];
  unallocatedAmount: any[][] = [[], []];

  // Flag to prevent multiple initialization
  private isInitialized = false;
  private isFetchingImports = false;

  // Tabs configuration
  tabs: any[] = [
    { id: 1, name: 'Form Entry', url: '', active: true },
    { id: 2, name: 'Form View', url: '', active: false },
    { id: 3, name: 'Audit', url: '', active: false }
  ];
  activeTab: number = 1;

  // Input properties for integration
  rowData: any = {};
  colData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  screen_key: any;
  process_name: string = '';

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120lSchFService: F1120lSchFService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || header.userSettings || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.colData = activeScreen?.data?.colData || {};
    this.globalService.filterParams = this.filterParams;
    const prevProcess = this.process_name;
    this.screen_key = this.filterParams?.screen_key || this.globalService?.globalParams?.screen_key;
    this.process_name = this.filterParams?.process_name || this.globalService?.globalParams?.process_name || '';
    if (this.isInitialized && prevProcess !== this.process_name && this.process_name) {
      this.fetchSchFimports();
    }
    this.isInitialized = false;
    this.cdr.markForCheck();
  }

  init() {
    try {
      // CRITICAL: Wait for globalService and GENERAL_CONFIG to be ready
      if (!this.globalService || !this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
        return;
      }

      // Only proceed if we have row data and not already initialized
      if (this.rowData && Object.keys(this.rowData).length > 0 && !this.isInitialized) {
        this.isInitialized = true;
        
        // Setup basic entity properties
        this.leId = this.rowData.LEID;
        this.cdr_no = this.rowData.MARS;
        this.leType = this.rowData.LE_TYPE;
        
        // CRITICAL: Access globalParams properties from globalService
        this.screen_key = this.filterParams?.screen_key || this.globalService?.globalParams?.screen_key;
        this.process_name = this.filterParams?.process_name || this.globalService?.globalParams?.process_name || '';

        // Setup entity list as array for ngFor
        this.entityList = [
          { key: "Tax Year", value: this.rowData.TAX_YEAR },
          { key: "LEID", value: this.rowData.LEID },
          { key: "LE Name", value: this.rowData.LE_NAME },
          { key: "LE TYPE", value: this.rowData.LE_TYPE },
          { key: "ME", value: this.rowData.ME }
        ];

        // Setup unallocated amount
        this.unallocatedAmount = this.rowData.dig || [[], []];
        
        this.fetchSchFrecords();
        this.fetchSchFimports();
      }
    } catch (e) {
      console.error('Error in F1120L-SCH-F init:', e);
    }
  }

  fetchSchFrecords() {
    const params = {
      "action_id": 603,
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.irsForm && data.irsForm.formItemList) {
          // Get modal title from service response
          if (data.irsForm.formItemList[0] && data.irsForm.formItemList[0].dataRowList[0]) {
            this.modalTitle = data.irsForm.formItemList[0].dataRowList[0].dataCellList[1].name;
          }

          this.scheduleFFormView.rows = [];

          for (let i = 2; i < data.irsForm.formItemList.length; i++) {
            const saveParamDtls = data.irsForm.formItemList[i].dataRowList[0].rowDimensions.object_id.split('~');
            const tempDataObj: any = {
              saveParams: saveParamDtls
            };

            const dataCellList = data.irsForm.formItemList[i].dataRowList[0].dataCellList;

            for (let j = 0; j < dataCellList.length; j++) {
              if (j === 0) {
                tempDataObj.num = dataCellList[j].name;
              } else if (j === 1) {
                tempDataObj.description = dataCellList[j].name;
              } else if (j === 2) {
                tempDataObj.lineAttrKeyA = dataCellList[j].key.split('~')[1];
                tempDataObj.attrib_name_a = dataCellList[j].key.split('~')[0];
                tempDataObj.attrib_order_a = dataCellList[j].key.split('~')[2];
                tempDataObj.is_editable = dataCellList[j].property.is_editable_cell !== 'Y';
                tempDataObj.value1 = dataCellList[j].value ? parseInt(dataCellList[j].value) : 0;
              } else if (j === 3) {
                tempDataObj.lineAttrKeyB = dataCellList[j].key.split('~')[1];
                tempDataObj.attrib_name_b = dataCellList[j].key.split('~')[0];
                tempDataObj.attrib_order_b = dataCellList[j].key.split('~')[2];
                tempDataObj.is_editable = dataCellList[j].property.is_editable_cell !== 'Y';
                tempDataObj.value2 = dataCellList[j].value ? parseInt(dataCellList[j].value) : 0;
              } else {
                tempDataObj.lineAttrKeyC = dataCellList[j].key.split('~')[1];
                tempDataObj.attrib_name_c = dataCellList[j].key.split('~')[0];
                tempDataObj.attrib_order_c = dataCellList[j].key.split('~')[2];
                tempDataObj.is_editable3 = dataCellList[j].property.is_editable_cell !== 'Y';
                tempDataObj.value3 = dataCellList[j].value ? parseInt(dataCellList[j].value) : 0;
              }
            }

            tempDataObj.line_order = saveParamDtls[8];
            this.scheduleFFormView.rows.push(tempDataObj);
          }

          this.formListOriginal = _.cloneDeep(this.scheduleFFormView);
          // CRITICAL: Save original unallocatedAmount for reset functionality
          this.unallocatedAmountOriginal = _.cloneDeep(this.unallocatedAmount);
          this.computeValues(null);
          this.showFormLoader = false;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error fetching Schedule F records:', error);
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  fetchSchFimports() {
    if (this.isFetchingImports) {
      return;
    }
    
    const clientKey = this.USER_SETTINGS?.user?.client_key || this.globalService?.globalParams?.client_key;
    const params = {
      "client_key": clientKey,
      "process_name": this.process_name || '1120L Sch F',
      "status": 'Y'
    };
    
    this.isFetchingImports = true;

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=wwledr',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject) {
          this.BatchList = data.jsonObject;
          this.BatchList.forEach((value, key) => {
            this.BatchList[key].TOGGLE = false;
          });
        }
        this.showRequestLoader = false;
        this.isFetchingImports = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error fetching Schedule F imports:', error);
        this.showRequestLoader = false;
        this.isFetchingImports = false;
        this.cdr.detectChanges();
      }
    );
  }

  loadTransactions(index: number, trans_key: string) {
    this.batchLoader = true;
    const clientKey = this.USER_SETTINGS?.user?.client_key || this.globalService?.globalParams?.client_key;
    const params = {
      "client_key": clientKey,
      "process_name": this.process_name || '1120L Sch F',
      "transaction_key": trans_key
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=l6gzle',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject) {
          const tempData = data.jsonObject;
          let currLineNo: any;
          let tempBatchList: any = {};
          const tempBatchData: any[] = [];

          tempData.forEach((value: any, key: number) => {
            if (key === 0) {
              currLineNo = tempData[key].LINE_NO;
              tempBatchList = { ...tempData[key] };
            } else {
              if (tempData[key].LINE_NO === currLineNo) {
                tempBatchList.ATTRIBUTE_VALUE_B = tempData[key].ATTRIBUTE_VALUE;
                tempBatchData.push({ ...tempBatchList });
                tempBatchList = {};
              } else {
                currLineNo = tempData[key].LINE_NO;
                tempBatchList = { ...tempData[key] };
              }
            }
          });
          
          // Push last item if exists
          if (Object.keys(tempBatchList).length > 0) {
            tempBatchData.push({ ...tempBatchList });
          }

          this.BatchListData = tempBatchData;
        }
        this.batchLoader = false;
        this.showBatchData = true;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading transactions:', error);
        this.batchLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  showUploads(index: number, trans_key: string) {
    if (this.prev_index !== null) {
      if (this.prev_index !== index) {
        this.BatchList[this.prev_index].TOGGLE = false;
        this.prev_index = index;
        this.showBatchData = false;
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.loadTransactions(index, trans_key);
      } else {
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.showBatchData = true;
      }
    } else {
      this.prev_index = index;
      this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
      this.loadTransactions(index, trans_key);
    }
  }

  computeValues(row: any) {
    const taxYear = this.rowData.TAX_YEAR;
    
    switch (taxYear) {
      case 2017:
        this.computeValues2017(row);
        break;
      case 2018:
        this.computeValues2018(row);
        break;
      case 2019:
        this.computeValues2019(row);
        break;
      default:
        this.computeValues2019(row);
    }
  }

  computeValues2019(row: any) {
    // for NaN issue
    for (let i = 0; i < 12; i++) {
      this.scheduleFFormView.rows[i].value1 = this.scheduleFFormView.rows[i].value1 || 0;
      this.scheduleFFormView.rows[i].value2 = this.scheduleFFormView.rows[i].value2 || 0;
    }

    // Line 7 a & b: add lines 1 through 6
    this.scheduleFFormView.rows[6].value1 = 0;
    this.scheduleFFormView.rows[6].value2 = 0;
    for (let i = 0; i < 6; i++) {
      this.scheduleFFormView.rows[6].value1 += Math.round(this.scheduleFFormView.rows[i].value1);
      this.scheduleFFormView.rows[6].value2 += Math.round(this.scheduleFFormView.rows[i].value2);
    }

    // Line 8
    //a
    this.scheduleFFormView.rows[7].value1 = "";
    //b
    this.scheduleFFormView.rows[7].value2 =
      this.scheduleFFormView.rows[6].value2 - this.scheduleFFormView.rows[6].value1;

    //Line 11
    //a
    this.scheduleFFormView.rows[10].value1 = "";
    //b
    this.scheduleFFormView.rows[10].value2 =
      Math.round(this.scheduleFFormView.rows[8].value1 * 0.3);

    //Line 12
    this.scheduleFFormView.rows[11].value2 =
      this.scheduleFFormView.rows[7].value2 - (this.scheduleFFormView.rows[10].value2 + this.scheduleFFormView.rows[10].value3);

    this.netReserves = this.scheduleFFormView.rows[11].value2;

    // Update unallocated amount if available
    if (this.unallocatedAmount && this.unallocatedAmount.length >= 2) {
      if (this.unallocatedAmount[0] && this.unallocatedAmount[0][0]) {
        this.unallocatedAmount[0][0].value = this.scheduleFFormView.rows[11].value2;
      }
      if (this.unallocatedAmount[1] && this.unallocatedAmount[1][0]) {
        this.unallocatedAmount[1][0].value = this.scheduleFFormView.rows[11].value2;
      }
      if (this.unallocatedAmount[0] && this.unallocatedAmount[0][2]) {
        this.unallocatedAmount[0][2].value = this.unallocatedAmount[0][0].value - this.unallocatedAmount[0][1].value;
      }
      if (this.unallocatedAmount[1] && this.unallocatedAmount[1][2]) {
        this.unallocatedAmount[1][2].value = this.unallocatedAmount[1][0].value - this.unallocatedAmount[1][1].value;
      }
    }
  }

  computeValues2018(row: any) {
    // for NaN issue
    for (let i = 0; i < 12; i++) {
      this.scheduleFFormView.rows[i].value1 = this.scheduleFFormView.rows[i].value1 || 0;
      this.scheduleFFormView.rows[i].value2 = this.scheduleFFormView.rows[i].value2 || 0;
    }

    // Line 7 a & b: add lines 1 through 6
    this.scheduleFFormView.rows[6].value1 = 0;
    this.scheduleFFormView.rows[6].value2 = 0;
    for (let i = 0; i < 6; i++) {
      this.scheduleFFormView.rows[6].value1 += Math.round(this.scheduleFFormView.rows[i].value1);
      this.scheduleFFormView.rows[6].value2 += Math.round(this.scheduleFFormView.rows[i].value2);
    }

    // Line 8
    //a
    this.scheduleFFormView.rows[7].value1 = "";
    //b
    this.scheduleFFormView.rows[7].value2 =
      this.scheduleFFormView.rows[6].value2 - this.scheduleFFormView.rows[6].value1;

    //Line 11
    //a
    this.scheduleFFormView.rows[10].value1 = "";
    //b
    this.scheduleFFormView.rows[10].value2 =
      Math.round(this.scheduleFFormView.rows[8].value1 * 0.3);

    //Line 12
    this.scheduleFFormView.rows[11].value2 =
      this.scheduleFFormView.rows[7].value2 - (this.scheduleFFormView.rows[10].value2 + this.scheduleFFormView.rows[10].value3);

    this.netReserves = this.scheduleFFormView.rows[11].value2;

    // Update unallocated amount if available
    if (this.unallocatedAmount && this.unallocatedAmount.length >= 2) {
      if (this.unallocatedAmount[0] && this.unallocatedAmount[0][0]) {
        this.unallocatedAmount[0][0].value = this.scheduleFFormView.rows[11].value2;
      }
      if (this.unallocatedAmount[1] && this.unallocatedAmount[1][0]) {
        this.unallocatedAmount[1][0].value = this.scheduleFFormView.rows[11].value2;
      }
      if (this.unallocatedAmount[0] && this.unallocatedAmount[0][2]) {
        this.unallocatedAmount[0][2].value = this.unallocatedAmount[0][0].value - this.unallocatedAmount[0][1].value;
      }
      if (this.unallocatedAmount[1] && this.unallocatedAmount[1][2]) {
        this.unallocatedAmount[1][2].value = this.unallocatedAmount[1][0].value - this.unallocatedAmount[1][1].value;
      }
    }
  }

  computeValues2017(row: any) {
    this.scheduleFFormView.rows[6].value1 = this.scheduleFFormView.rows[0].value1 + this.scheduleFFormView.rows[1].value1 +
      this.scheduleFFormView.rows[2].value1 + this.scheduleFFormView.rows[3].value1 +
      this.scheduleFFormView.rows[4].value1 + this.scheduleFFormView.rows[5].value1;
    
    //Line 7 b (add line 1b to line 6b )
    this.scheduleFFormView.rows[6].value2 = this.scheduleFFormView.rows[0].value2 + this.scheduleFFormView.rows[1].value2 +
      this.scheduleFFormView.rows[2].value2 + this.scheduleFFormView.rows[3].value2 +
      this.scheduleFFormView.rows[4].value2 + this.scheduleFFormView.rows[5].value2;

    //Line 8 (Line 7b- 7a)
    this.scheduleFFormView.rows[7].value2 = this.scheduleFFormView.rows[6].value2 - this.scheduleFFormView.rows[6].value1;
    
    // Line 10f F (Line )
    this.scheduleFFormView.rows[14].value2 = this.scheduleFFormView.rows[9].value1 + this.scheduleFFormView.rows[10].value1 +
      this.scheduleFFormView.rows[11].value1 + this.scheduleFFormView.rows[12].value1 +
      this.scheduleFFormView.rows[13].value1;
    
    // Line 11
    this.scheduleFFormView.rows[15].value2 = this.scheduleFFormView.rows[8].value2 - this.scheduleFFormView.rows[14].value2;
    
    // Line 14
    this.scheduleFFormView.rows[18].value2 = this.scheduleFFormView.rows[16].value1 + this.scheduleFFormView.rows[17].value1;
    
    //Line 15
    if (this.scheduleFFormView.rows[7].value2 < 0) {
      this.scheduleFFormView.rows[19].value2 = 0;
    } else {
      this.scheduleFFormView.rows[19].value2 = this.scheduleFFormView.rows[7].value2;
    }
    
    //Line 16
    this.scheduleFFormView.rows[20].value2 = this.scheduleFFormView.rows[18].value2 - this.scheduleFFormView.rows[19].value2;
    
    // line 17
    const temp = this.scheduleFFormView.rows[15].value2 / this.scheduleFFormView.rows[20].value2;
    if (temp < 0) {
      this.scheduleFFormView.rows[21].value2 = 0;
    } else {
      this.scheduleFFormView.rows[21].value2 = temp;
    }
    
    //Line 18b
    this.scheduleFFormView.rows[23].value1 = this.scheduleFFormView.rows[10].value1;

    // Line 18e (Line )
    this.scheduleFFormView.rows[26].value2 = this.scheduleFFormView.rows[22].value1 + this.scheduleFFormView.rows[23].value1 +
      this.scheduleFFormView.rows[24].value1 + this.scheduleFFormView.rows[25].value1;
    
    //Line 19
    this.scheduleFFormView.rows[27].value1 = this.scheduleFFormView.rows[10].value1;

    // Line 23 (Line )
    this.scheduleFFormView.rows[31].value2 = this.scheduleFFormView.rows[27].value1 + this.scheduleFFormView.rows[28].value1 +
      this.scheduleFFormView.rows[29].value1 + this.scheduleFFormView.rows[30].value1;
    
    //Line 24
    this.scheduleFFormView.rows[32].value2 = this.scheduleFFormView.rows[26].value2 - this.scheduleFFormView.rows[31].value2;

    //Line 25
    this.scheduleFFormView.rows[33].value2 = this.scheduleFFormView.rows[21].value2 * this.scheduleFFormView.rows[32].value2;
    
    //Line 26
    this.scheduleFFormView.rows[34].value2 = this.scheduleFFormView.rows[14].value2;
    
    //Line 27
    this.scheduleFFormView.rows[35].value2 = this.scheduleFFormView.rows[34].value2 + this.scheduleFFormView.rows[33].value2;
    
    // Line 29
    this.scheduleFFormView.rows[37].value2 = this.scheduleFFormView.rows[35].value2;
    
    //Line 30
    this.scheduleFFormView.rows[38].value2 = this.scheduleFFormView.rows[36].value2 - this.scheduleFFormView.rows[37].value2;
    
    //Line 31
    this.scheduleFFormView.rows[39].value2 = 100;
    
    //Line 32
    if (Number(this.scheduleFFormView.rows[36].value2) === 0) {
      this.scheduleFFormView.rows[40].value2 = 0;
    } else {
      this.scheduleFFormView.rows[40].value2 = this.scheduleFFormView.rows[38].value2 / this.scheduleFFormView.rows[36].value2;
    }
    
    //Line 33
    this.scheduleFFormView.rows[41].value2 = this.scheduleFFormView.rows[39].value2 - this.scheduleFFormView.rows[40].value2;

    //Line 34
    this.scheduleFFormView.rows[42].value2 = this.scheduleFFormView.rows[41].value2 + this.scheduleFFormView.rows[17].value2;
    
    //Line 35
    this.scheduleFFormView.rows[43].value2 = this.scheduleFFormView.rows[7].value2 + this.scheduleFFormView.rows[42].value2;
    
    this.netReserves = this.scheduleFFormView.rows[43].value2;
  }

  save() {
    const formRowDataA: any = {};
    const formRowDataB: any = {};
    const formRowDataC: any = {};
    const tempArray: any[] = [];

    for (let i = 0; i < this.scheduleFFormView.rows.length; i++) {
      const row = this.scheduleFFormView.rows[i];
      
      const formRowDataA = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_a,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKeyA,
        Attribute_Name: row.attrib_name_a,
        Attribute_Value: row.value1,
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };

      const formRowDataB = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_b,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKeyB,
        Attribute_Name: row.attrib_name_b,
        Attribute_Value: row.value2,
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };

      tempArray.push(formRowDataA);
      tempArray.push(formRowDataB);

      if (typeof row.value3 !== "undefined") {
        const formRowDataC = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: row.saveParams[1],
          line_order: row.line_order,
          attrib_order: row.attrib_order_c,
          line_description: row.description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: row.saveParams[2],
          line_no: row.num,
          occurence: row.saveParams[7],
          line_attrib_key: row.lineAttrKeyC,
          Attribute_Name: row.attrib_name_c,
          Attribute_Value: row.value3,
          trans_dtls_key: row.saveParams[3],
          trans_status: row.saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataC);
      }
    }

    this.formData.editrows.push(tempArray);
    const mainData: any[] = [];
    mainData.push(this.formData);

    // Build diagnostic data
    const diagData: any[] = [];
    if (this.unallocatedAmount) {
      this.unallocatedAmount.forEach((array: any[], key: number) => {
        array.forEach((obj: any, idx: number) => {
          if (idx === 3) {
            diagData.push(obj);
          }
        });
      });
    }

    // CRITICAL: Call service with proper parameters like F1120L-SCH-B
    this.f1120lSchFService.saveSchddForm(
      this.GENERAL_CONFIG.base_url, 
      mainData,
      diagData,
      this.globalService, 
      this.filterParams, 
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error saving form:', error);
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  reset() {
    this.scheduleFFormView = _.cloneDeep(this.formListOriginal);
    // CRITICAL: Reset unallocatedAmount including exception_flag and exception_msg
    this.unallocatedAmount = _.cloneDeep(this.unallocatedAmountOriginal);
    this.computeValues(null);
  }

  // TrackBy function for performance
  itemTracker(index: number, item: any): any {
    return index;
  }

  // Update value methods for form inputs
  updateValue1(row: any, event: any) {
    const value = event.target.value;
    row.value1 = value ? parseInt(value) : 0;
    this.computeValues(row);
  }

  updateValue2(row: any, event: any) {
    const value = event.target.value;
    row.value2 = value ? parseInt(value) : 0;
    this.computeValues(row);
  }

  updateValue3(row: any, event: any) {
    const value = event.target.value;
    row.value3 = value ? parseInt(value) : 0;
    this.computeValues(row);
  }

  // Number formatting function
  numberWithCommas(x: any): string {
    if (x === null || x === undefined || x === '' || x === 'undefined') {
      return '';
    }
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
}

